import { useEffect, useState } from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import Parse from "../api/config";
import { useAuth } from "./AuthContext";
import Modal from "./Modal";
import { JobApplicationsManager } from "./JobApplicationsManager";

export const Administrator = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [userRoles, setUserRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeRole, setActiveRole] = useState(null);
    const [activeTab, setActiveTab] = useState('approvals'); // New state for tab management
    const [pendingForms, setPendingForms] = useState([]);
    const [approvedForms, setApprovedForms] = useState([]);
    const [deniedForms, setDeniedForms] = useState([]);
    const [cancelledForms, setCancelledForms] = useState([]);
    const [selectedForm, setSelectedForm] = useState(null);
    const [denialReason, setDenialReason] = useState("");
    const [showDenialModal, setShowDenialModal] = useState(false);
    const [formDetails, setFormDetails] = useState(null);
    const [showFormModal, setShowFormModal] = useState(false);

    // Fetch user roles when component mounts
    useEffect(() => {
        const fetchUserRoles = async () => {
            if (!user || !user.email) return;
            
            try {
                setLoading(true);
                const result = await Parse.Cloud.run('getUserRoles', { email: user.email });
                setUserRoles(result.roles || []);
                
                // Set active role to the first role if available
                if (result.roles && result.roles.length > 0) {
                    setActiveRole(result.roles[0]);
                }
            } catch (error) {
                console.error('Error fetching user roles:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchUserRoles();
    }, [user]);

    // Fetch forms when active role changes
    useEffect(() => {
        if (!activeRole) return;
        
        const fetchForms = async () => {
            try {
                setLoading(true);

                // Fetch pending forms
                const pendingResult = await Parse.Cloud.run('getPendingFormsForRole', { role: activeRole });
                setPendingForms(pendingResult.forms || []);

                // Fetch approved forms
                const approvedResult = await Parse.Cloud.run('getApprovedFormsByRole', { role: activeRole });
                setApprovedForms(approvedResult.forms || []);

                // Fetch denied forms
                const deniedResult = await Parse.Cloud.run('getDeniedFormsByRole', { role: activeRole });
                setDeniedForms(deniedResult.forms || []);

                // Fetch cancelled forms
                const cancelledResult = await Parse.Cloud.run('getCancelledFormsByRole', { role: activeRole });
                setCancelledForms(cancelledResult.forms || []);
            } catch (error) {
                console.error('Error fetching forms:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchForms();
    }, [activeRole]);

    const handleRoleChange = (role) => {
        setActiveRole(role);
    };

    const openFormDetails = async (formId) => {
        try {
            setLoading(true);

            // Fetch form details and approval status
            const result = await Parse.Cloud.run('getFormApprovalStatus', { formId });

            if (result.success) {
                setFormDetails(result);
                setShowFormModal(true);
            } else {
                console.error('Error fetching form details:', result.message);
            }
        } catch (error) {
            console.error('Error fetching form details:', error);
        } finally {
            setLoading(false);
        }
    };

    const openFullFormView = (formId) => {
        // Navigate to the form page to view in full detail
        navigate(`/home/form/${formId}`);
    };

    const handleApproveForm = async (formId) => {
        try {
            setLoading(true);
            
            const result = await Parse.Cloud.run('approveForm', { 
                formId,
                role: activeRole
            });
            
            if (result.success) {
                // Refresh forms after approval
                const pendingResult = await Parse.Cloud.run('getPendingFormsForRole', { role: activeRole });
                setPendingForms(pendingResult.forms || []);
                
                const approvedResult = await Parse.Cloud.run('getApprovedFormsByRole', { role: activeRole });
                setApprovedForms(approvedResult.forms || []);
                
                // Close any open modals
                setShowFormModal(false);
            } else {
                console.error('Error approving form:', result.message);
                alert(`Error approving form: ${result.message}`);
            }
        } catch (error) {
            console.error('Error approving form:', error);
            alert(`Error approving form: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const openDenialModal = (formId) => {
        setSelectedForm(formId);
        setDenialReason("");
        setShowDenialModal(true);
    };

    const handleDenyForm = async () => {
        if (!selectedForm || !denialReason) {
            alert("Please provide a reason for denial");
            return;
        }
        
        try {
            setLoading(true);
            
            const result = await Parse.Cloud.run('denyForm', { 
                formId: selectedForm,
                role: activeRole,
                reason: denialReason
            });
            
            if (result.success) {
                // Refresh forms after denial
                const pendingResult = await Parse.Cloud.run('getPendingFormsForRole', { role: activeRole });
                setPendingForms(pendingResult.forms || []);

                const deniedResult = await Parse.Cloud.run('getDeniedFormsByRole', { role: activeRole });
                setDeniedForms(deniedResult.forms || []);

                // Fetch cancelled forms
                const cancelledResult = await Parse.Cloud.run('getCancelledFormsByRole', { role: activeRole });
                setCancelledForms(cancelledResult.forms || []);

                // Close denial modal
                setShowDenialModal(false);
                setSelectedForm(null);
                setDenialReason("");

                // Close any open form details modal
                setShowFormModal(false);
            } else {
                console.error('Error denying form:', result.message);
                alert(`Error denying form: ${result.message}`);
            }
        } catch (error) {
            console.error('Error denying form:', error);
            alert(`Error denying form: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const generatePdf = async (formId) => {
        try {
            setLoading(true);

            const result = await Parse.Cloud.run('generatePDF', { formId });

            if (result && result.url) {
                // Open PDF in a new tab
                window.open(result.url, '_blank');
            } else {
                throw new Error('Failed to generate PDF');
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('Error generating PDF. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const sendApprovalEmails = async (formId) => {
        if (!window.confirm('Are you sure you want to send approval request emails for this form? This will notify all pending approvers.')) {
            return;
        }

        try {
            setLoading(true);

            const result = await Parse.Cloud.run('sendApprovalEmails', { formId });

            if (result.success) {
                alert(`✅ ${result.message}\n\nEmails sent: ${result.emailsSent}\nPending roles: ${result.pendingRoles.join(', ')}`);
            } else {
                alert(`❌ ${result.message}`);
            }
        } catch (error) {
            console.error('Error sending approval emails:', error);
            alert('❌ Error sending approval emails. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return format(date, 'MMMM dd, yyyy');
    };

    if (loading && userRoles.length === 0) {
        return (
            <div className="container mx-auto p-4 flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (userRoles.length === 0) {
        return (
            <div className="container mx-auto p-4">
                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4" role="alert">
                    <p className="font-bold">No Approval Roles</p>
                    <p>You don't have any roles assigned for form approvals. Please contact your administrator.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4">
            {/* Main Tab Navigation */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold mb-4">Administrator</h1>
                <div className="flex gap-4 border-b border-gray-200 mb-4">
                    <button
                        className={`px-4 py-2 font-medium text-sm ${
                            activeTab === 'approvals'
                                ? 'border-b-2 border-indigo-600 text-indigo-600'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                        onClick={() => setActiveTab('approvals')}
                    >
                        Form Approvals
                    </button>
                    <button
                        className={`px-4 py-2 font-medium text-sm ${
                            activeTab === 'applications'
                                ? 'border-b-2 border-indigo-600 text-indigo-600'
                                : 'text-gray-600 hover:text-gray-800'
                        }`}
                        onClick={() => setActiveTab('applications')}
                    >
                        Job Applications
                    </button>
                </div>
            </div>

            {/* Conditionally render content based on active tab */}
            {activeTab === 'applications' ? (
                <JobApplicationsManager />
            ) : (
                <>
                    {/* Roles Tabs for Form Approvals */}
                    {userRoles.length > 0 && (
                        <div className="mb-6">
                            <h2 className="text-xl font-semibold mb-3">Role Approvals</h2>
                            <div className="flex flex-wrap gap-2 border-b border-gray-200">
                                {userRoles.map(role => (
                                    <button
                                        key={role}
                                        className={`px-4 py-2 font-medium text-sm rounded-t-lg ${
                                            activeRole === role
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                        onClick={() => handleRoleChange(role)}
                                    >
                                        {role}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

            {/* Loading indicator */}
            {loading && (
                <div className="flex justify-center my-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
            )}

            {/* Pending Approvals */}
            <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4">Pending Approvals</h2>
                {pendingForms.length === 0 ? (
                    <p className="text-gray-500 italic">No forms pending approval</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-200">
                            <thead>
                                <tr>
                                    <th className="py-2 px-4 border-b min-w-[200px]">Event Name</th>
                                    <th className="py-2 px-4 border-b min-w-[120px]">Department</th>
                                    <th className="py-2 px-4 border-b min-w-[100px]">Date</th>
                                    <th className="py-2 px-4 border-b min-w-[150px]">Location</th>
                                    <th className="py-2 px-4 border-b min-w-[180px]">Submitted By</th>
                                    <th className="py-2 px-4 border-b min-w-[320px]">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pendingForms.map(form => (
                                    <tr key={form.id} className="hover:bg-gray-100">
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.eventName}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.department}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{formatDate(form.date)}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.location}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">
                                            {form.owner ? form.owner.email : 'Unknown'}
                                        </td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap space-x-2">
                                            <button
                                                onClick={() => openFullFormView(form.id)}
                                                className="px-2 py-1 bg-indigo-500 text-white rounded hover:bg-indigo-600"
                                            >
                                                View Form
                                            </button>
                                            <button
                                                onClick={() => openFormDetails(form.id)}
                                                className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                            >
                                                View Status
                                            </button>
                                            <button
                                                onClick={() => handleApproveForm(form.id)}
                                                className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                                            >
                                                Approve
                                            </button>
                                            <button
                                                onClick={() => openDenialModal(form.id)}
                                                className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                                            >
                                                Deny
                                            </button>
                                            <button
                                                onClick={() => sendApprovalEmails(form.id)}
                                                className="hidden px-2 py-1 bg-amber-500 text-white rounded hover:bg-amber-600"
                                                title="Send approval request emails to pending approvers"
                                            >
                                                📧 Send Emails
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Approved Forms */}
            <div>
                <h2 className="text-xl font-semibold mb-4">Approved Forms</h2>
                {approvedForms.length === 0 ? (
                    <p className="text-gray-500 italic">No approved forms</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-200">
                            <thead>
                                <tr>
                                    <th className="py-2 px-4 border-b min-w-[200px]">Event Name</th>
                                    <th className="py-2 px-4 border-b min-w-[120px]">Department</th>
                                    <th className="py-2 px-4 border-b min-w-[100px]">Date</th>
                                    <th className="py-2 px-4 border-b min-w-[150px]">Location</th>
                                    <th className="py-2 px-4 border-b min-w-[120px]">Approved On</th>
                                    <th className="py-2 px-4 border-b min-w-[140px]">Status</th>
                                    <th className="py-2 px-4 border-b min-w-[280px]">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {approvedForms.map(form => (
                                    <tr key={form.id} className="hover:bg-gray-100">
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.eventName}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.department}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{formatDate(form.date)}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.location}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{formatDate(form.approvedAt)}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                form.isFullyApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {form.isFullyApproved ? 'Fully Approved' : 'Partially Approved'}
                                            </span>
                                        </td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap space-x-2">
                                            <button
                                                onClick={() => openFullFormView(form.id)}
                                                className="px-2 py-1 bg-indigo-500 text-white rounded hover:bg-indigo-600"
                                            >
                                                View Form
                                            </button>
                                            <button
                                                onClick={() => openFormDetails(form.id)}
                                                className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                            >
                                                View Status
                                            </button>
                                            <button
                                                onClick={() => generatePdf(form.id)}
                                                className="px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                                            >
                                                PDF
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Denied Forms */}
            <div>
                <h2 className="text-xl font-semibold mb-4">Denied Forms</h2>
                {deniedForms.length === 0 ? (
                    <p className="text-gray-500 italic">No denied forms</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-200">
                            <thead>
                                <tr>
                                    <th className="py-2 px-4 border-b min-w-[200px]">Event Name</th>
                                    <th className="py-2 px-4 border-b min-w-[120px]">Department</th>
                                    <th className="py-2 px-4 border-b min-w-[100px]">Date</th>
                                    <th className="py-2 px-4 border-b min-w-[150px]">Location</th>
                                    <th className="py-2 px-4 border-b min-w-[120px]">Denied By</th>
                                    <th className="py-2 px-4 border-b min-w-[120px]">Denied At</th>
                                    <th className="py-2 px-4 border-b min-w-[200px]">Reason</th>
                                    <th className="py-2 px-4 border-b min-w-[240px]">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deniedForms.map(form => (
                                    <tr key={form.id} className="hover:bg-gray-100">
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.eventName}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.department}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{formatDate(form.date)}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.location}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.deniedBy}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{formatDate(form.deniedAt)}</td>
                                        <td className="py-2 px-4 border-b">
                                            <div className="max-w-xs overflow-hidden text-ellipsis" title={form.denialReason}>
                                                {form.denialReason ?
                                                    (form.denialReason.length > 50 ?
                                                        `${form.denialReason.substring(0, 50)}...` :
                                                        form.denialReason
                                                    ) :
                                                    'No reason provided'
                                                }
                                            </div>
                                        </td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap space-x-2">
                                            <button
                                                onClick={() => openFullFormView(form.id)}
                                                className="px-2 py-1 bg-indigo-500 text-white rounded hover:bg-indigo-600"
                                            >
                                                View Form
                                            </button>
                                            <button
                                                onClick={() => openFormDetails(form.id)}
                                                className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                            >
                                                View Status
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Cancelled Forms */}
            <div>
                <h2 className="text-xl font-semibold mb-4">Cancelled Forms</h2>
                {cancelledForms.length === 0 ? (
                    <p className="text-gray-500 italic">No cancelled forms</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-200">
                            <thead>
                                <tr>
                                    <th className="py-2 px-4 border-b min-w-[200px]">Event Name</th>
                                    <th className="py-2 px-4 border-b min-w-[120px]">Department</th>
                                    <th className="py-2 px-4 border-b min-w-[100px]">Date</th>
                                    <th className="py-2 px-4 border-b min-w-[180px]">Submitted By</th>
                                    <th className="py-2 px-4 border-b min-w-[150px]">Cancelled At</th>
                                    <th className="py-2 px-4 border-b min-w-[200px]">Reason</th>
                                    <th className="py-2 px-4 border-b min-w-[140px]">Status</th>
                                    <th className="py-2 px-4 border-b min-w-[240px]">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cancelledForms.map((form, index) => (
                                    <tr key={index} className="hover:bg-gray-100">
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.eventName || 'Untitled Event'}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.department}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">{form.date}</td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">
                                            {form.owner ? form.owner.email : 'Unknown'}
                                        </td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">
                                            {form.cancelledAt ? format(new Date(form.cancelledAt), 'MM/dd/yyyy HH:mm') : 'Unknown'}
                                        </td>
                                        <td className="py-2 px-4 border-b">
                                            <div className="max-w-xs">
                                                {form.cancellationReason ?
                                                    (form.cancellationReason.length > 50 ?
                                                        `${form.cancellationReason.substring(0, 50)}...` :
                                                        form.cancellationReason
                                                    ) :
                                                    'No reason provided'
                                                }
                                            </div>
                                        </td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap">
                                            <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">
                                                🚫 Cancelled
                                            </span>
                                            {form.isFullyApproved && (
                                                <span className="ml-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">
                                                    Was Approved
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2 px-4 border-b whitespace-nowrap space-x-2">
                                            <button
                                                onClick={() => openFullFormView(form.id)}
                                                className="px-2 py-1 bg-indigo-500 text-white rounded hover:bg-indigo-600"
                                            >
                                                View Form
                                            </button>
                                            <button
                                                onClick={() => openFormDetails(form.id)}
                                                className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                            >
                                                View Status
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
                </>
            )}

            {/* Denial Modal */}
            {showDenialModal && (
                <Modal 
                    isOpen={showDenialModal} 
                    onClose={() => setShowDenialModal(false)}
                    title="Deny Form Request"
                >
                    <div className="p-4">
                        <p className="mb-4">Please provide a reason for denying this form request:</p>
                        <textarea
                            value={denialReason}
                            onChange={(e) => setDenialReason(e.target.value)}
                            className="w-full h-32 p-2 border border-gray-300 rounded mb-4"
                            placeholder="Enter reason for denial..."
                        />
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => setShowDenialModal(false)}
                                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDenyForm}
                                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                                disabled={!denialReason.trim()}
                            >
                                Deny Request
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Form Details Modal */}
            {showFormModal && formDetails && (
                <Modal 
                    isOpen={showFormModal} 
                    onClose={() => setShowFormModal(false)}
                    title={`Form Details: ${formDetails.formId}`}
                    size="lg"
                >
                    <div className="p-4">
                        <div className="mb-4">
                            <h3 className="text-lg font-semibold mb-2">Status</h3>
                            <div className={`px-3 py-1 rounded inline-block ${
                                formDetails.status === 'Denied' ? 'bg-red-100 text-red-800' :
                                formDetails.status === 'Fully Approved' ? 'bg-green-100 text-green-800' :
                                'bg-yellow-100 text-yellow-800'
                            }`}>
                                {formDetails.status}
                            </div>
                        </div>

                        {formDetails.isDenied && formDetails.denialInfo && (
                            <div className="mb-4 bg-red-50 p-3 rounded">
                                <h3 className="text-lg font-semibold mb-2">Denial Information</h3>
                                <p><strong>Denied by:</strong> {formDetails.denialInfo.role}</p>
                                <p><strong>Reason:</strong> {formDetails.denialInfo.reason}</p>
                                <p><strong>Denied on:</strong> {formatDate(formDetails.denialInfo.deniedAt)}</p>
                            </div>
                        )}

                        <div className="mb-4">
                            <h3 className="text-lg font-semibold mb-2">Required Approvals</h3>
                            <div className="flex flex-wrap gap-2">
                                {formDetails.requiredApprovals.map(role => {
                                    const isApproved = formDetails.completedApprovals.includes(role);
                                    return (
                                        <div 
                                            key={role} 
                                            className={`px-3 py-1 rounded ${
                                                isApproved ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                            }`}
                                        >
                                            {role} {isApproved && '✓'}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {formDetails.approvalDetails && formDetails.approvalDetails.length > 0 && (
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold mb-2">Approval History</h3>
                                <ul className="list-disc list-inside">
                                    {formDetails.approvalDetails.map((approval, index) => (
                                        <li key={index}>
                                            <strong>{approval.role}</strong> approved on {formatDate(approval.approvedAt)} by {approval.approverEmail}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {!formDetails.isDenied && !formDetails.isFullyApproved && (
                            <div className="flex justify-end space-x-2 mt-4">
                                <button
                                    onClick={() => openDenialModal(formDetails.formId)}
                                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                                >
                                    Deny
                                </button>
                                <button
                                    onClick={() => handleApproveForm(formDetails.formId)}
                                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                                >
                                    Approve
                                </button>
                            </div>
                        )}

                        {formDetails.pdfUrl && (
                            <div className="mt-4">
                                <a 
                                    href={formDetails.pdfUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 inline-block"
                                >
                                    View Approved PDF
                                </a>
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};